import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, QrCode, KeyRound, CheckCircle2, Loader2, Copy } from "lucide-react";

// TwoFactorSettings — Super Admin only.
// Fluxo: fetch status → setup (gera QR) → confirm com código → 2FA ON.
// Desativar exige código atual do autenticador (defesa contra sessão sequestrada).

export default function TwoFactorSettings() {
  const { user } = useAuth();
  const isSA = user?.role === "super_admin" || (user?.role === "admin" && !(user?.empresa_ids || []).length);
  const [status, setStatus] = useState(null); // { enabled: bool }
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null); // { qr_data_uri, manual_key }
  const [code, setCode] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/2fa/status");
      setStatus(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar status");
    } finally { setLoading(false); }
  };
  // `refresh` é definido no corpo do componente — adicioná-lo às deps sem useCallback
  // provocaria loop (nova identidade a cada render). Chamamos apenas quando isSA muda.
  useEffect(() => { if (isSA) refresh(); else setLoading(false); }, [isSA]);

  const startSetup = async () => {
    setSaving(true);
    try {
      const r = await api.post("/auth/2fa/setup");
      setSetupData(r.data);
      setCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao iniciar setup");
    } finally { setSaving(false); }
  };

  const confirmSetup = async () => {
    if (!/^\d{6}$/.test(code)) { toast.error("Informe o código de 6 dígitos"); return; }
    setSaving(true);
    try {
      await api.post("/auth/2fa/setup/confirm", { code });
      toast.success("✅ 2FA ativado com sucesso");
      setSetupData(null);
      setCode("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Código incorreto");
    } finally { setSaving(false); }
  };

  const disable = async () => {
    if (!/^\d{6}$/.test(code)) { toast.error("Informe o código de 6 dígitos"); return; }
    if (!window.confirm("Desativar 2FA? Sua conta ficará protegida apenas por senha.")) return;
    setSaving(true);
    try {
      await api.post("/auth/2fa/disable", { code });
      toast.success("2FA desativado");
      setCode("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Código incorreto");
    } finally { setSaving(false); }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(setupData?.manual_key || "");
      toast.success("Chave copiada");
    } catch (e) {
      console.warn("Clipboard bloqueado ou não suportado", e);
      toast.error("Falha ao copiar — copie manualmente");
    }
  };

  if (!isSA) {
    return (
      <div className="max-w-lg mx-auto mt-16 text-center" data-testid="page-2fa-denied">
        <ShieldOff className="w-16 h-16 mx-auto text-rose-500 mb-4" />
        <h1 className="text-2xl font-bold">Sem acesso</h1>
        <p className="text-muted-foreground mt-2">2FA está disponível apenas para o Super Admin.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="text-center py-16 text-muted-foreground">Carregando…</div>;
  }

  return (
    <div className="max-w-3xl space-y-6" data-testid="page-2fa">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-xl text-white ${status?.enabled ? "bg-emerald-600" : "bg-slate-500"}`}>
          <ShieldCheck className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-3xl font-extrabold leading-tight">Segurança · 2FA</h1>
          <p className="text-sm text-muted-foreground">
            Autenticação de dois fatores (TOTP) para o Super Admin.
          </p>
        </div>
        {status?.enabled ? (
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300" data-testid="badge-2fa-on">
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Ativado
          </Badge>
        ) : (
          <Badge className="bg-slate-100 text-slate-700 border-slate-300" data-testid="badge-2fa-off">Desativado</Badge>
        )}
      </div>

      {!status?.enabled && !setupData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><QrCode className="w-5 h-5 text-emerald-600" />Como funciona</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-slate-700">
            <ol className="list-decimal ml-4 space-y-1">
              <li>Instale um app autenticador (Google Authenticator, Authy, 1Password, Microsoft Authenticator).</li>
              <li>Clique em <b>Gerar QR Code</b> abaixo.</li>
              <li>Escaneie o QR com o app e digite o código de 6 dígitos exibido.</li>
              <li>A partir daí, todo login do Super Admin exigirá senha + código do app.</li>
            </ol>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
              <b>Importante:</b> guarde a chave manual num lugar seguro (gerenciador de senhas). Se perder o app, você poderá reconfigurar usando essa chave.
            </div>
            <Button onClick={startSetup} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-start-2fa">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Gerando…</> : <><QrCode className="w-4 h-4 mr-2" />Gerar QR Code</>}
            </Button>
          </CardContent>
        </Card>
      )}

      {setupData && !status?.enabled && (
        <Card>
          <CardHeader><CardTitle className="text-base">Escaneie e confirme</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col md:flex-row gap-6 items-center">
              <img src={setupData.qr_data_uri} alt="QR Code 2FA" className="w-52 h-52 rounded border-2 border-slate-200 bg-white" data-testid="img-qr-2fa" />
              <div className="flex-1 space-y-3 w-full">
                <div>
                  <Label className="text-xs">Chave manual (se não conseguir escanear)</Label>
                  <div className="flex gap-2 mt-1">
                    <code className="flex-1 px-3 py-2 bg-slate-100 rounded text-xs font-mono break-all" data-testid="manual-key">{setupData.manual_key}</code>
                    <Button size="sm" variant="outline" onClick={copyKey} data-testid="btn-copy-key">
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Código do autenticador (6 dígitos)</Label>
                  <Input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    placeholder="123456"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="mt-1 font-mono text-lg tracking-widest text-center"
                    data-testid="input-2fa-code"
                    autoFocus
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => { setSetupData(null); setCode(""); }}>Cancelar</Button>
                  <Button onClick={confirmSetup} disabled={saving || code.length !== 6} className="bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid="btn-confirm-2fa">
                    {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Confirmando…</> : "Ativar 2FA"}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {status?.enabled && (
        <Card className="border-emerald-200">
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><CheckCircle2 className="w-5 h-5 text-emerald-600" />2FA ativo</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-700">
              Seu login agora exige senha + código do autenticador. Para desativar, informe o código atual do app.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Código atual (6 dígitos)</Label>
                <Input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="mt-1 font-mono text-lg tracking-widest text-center"
                  data-testid="input-disable-code"
                />
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={disable}
                  disabled={saving || code.length !== 6}
                  className="border-rose-300 text-rose-700 hover:bg-rose-50 w-full"
                  data-testid="btn-disable-2fa"
                >
                  {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Desativando…</> : <><ShieldOff className="w-4 h-4 mr-2" />Desativar 2FA</>}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
